import React, { FormEvent, memo, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Column } from 'primereact/column'
import { DataTablePageEvent } from 'primereact/datatable'

import { ChargePoint, useCompanyGetChargePointsPagedQuery } from '../../../../../common/redux/api/web-api'
import { Dialog } from '../../../../../common/uikit/Dialog'
import { ChargepointStatus } from '../../../../../lib/chargepoint-status'
import localizeDate from '../../../../../common/utils/localizeDate'
import SettingsInfo from '../../../../../ui/chargepoints/SettingsInfo'
import { RootState } from '../../../../redux/store'
import { filterFieldsBySearchValue } from '../../../../../lib/filter-fields-by-search-value'
import { Input } from '../../../../../common/uikit/Input'
import useText from '../../../../../common/hooks/useText'
import { INITIAL_DEFAULT_TABLE_ROWS, Table } from '../../../../../common/uikit/Table'

export type AddChargePointToPoolDialogProps = {
    visible: boolean
    pendingChargePointIds: ReadonlySet<string>
    onAdd: (chargePoint: ChargePoint) => void
    onHide: () => void
}

export const AddChargePointToPoolDialog = memo(function AddChargePointToPoolDialog({
    visible,
    pendingChargePointIds,
    onAdd,
    onHide,
}: AddChargePointToPoolDialogProps): React.ReactElement {
    const text = useText()
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)
    const [draftSearch, setDraftSearch] = useState('')
    const [appliedSearch, setAppliedSearch] = useState('')
    const [first, setFirst] = useState(0)
    const [rows, setRows] = useState(INITIAL_DEFAULT_TABLE_ROWS)

    const onPage = (event: DataTablePageEvent) => {
        setFirst(event.first)
        setRows(event.rows)
    }

    const submitSearch = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setFirst(0)
        setAppliedSearch(draftSearch.trim())
    }

    const updateDraftSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const search = event.target.value
        setDraftSearch(search)

        if (search.trim() === '') {
            setFirst(0)
            setAppliedSearch('')
        }
    }

    const { data: chargePoints, isFetching: isLoadingChargePoints } = useCompanyGetChargePointsPagedQuery(
        {
            companyId: companyId ?? undefined,
            page: first / rows,
            noOfItems: rows,
        },
        { refetchOnMountOrArgChange: true, skip: !companyId || !visible }
    )

    const filteredChargePoints = useMemo(
        () =>
            chargePoints?.items?.filter((chargePoint) =>
                filterFieldsBySearchValue([chargePoint.id, chargePoint.name ?? undefined], appliedSearch)
            ) ?? [],
        [appliedSearch, chargePoints?.items]
    )
    const displayedChargePoints = useMemo(
        () =>
            filteredChargePoints.map((chargePoint) => ({
                ...chargePoint,
                isPending: pendingChargePointIds.has(chargePoint.id),
                isAssigned: !!chargePoint.poolId,
            })),
        [filteredChargePoints, pendingChargePointIds]
    )

    return (
        <Dialog
            header={text('lblAddChargePoint')}
            visible={visible}
            onHide={onHide}
            style={{ width: '80rem', maxHeight: '90vh' }}
            breakpoints={{ '960px': '96vw' }}
            contentStyle={{ overflow: 'visible' }}
            data-testid="add-charge-points-to-pool-dialog"
            footer={<Button type="button" label={text('btnCancel')} text onClick={onHide} />}
        >
            <form className="flex flex-column md:flex-row align-items-end gap-2 mb-2" onSubmit={submitSearch}>
                <Input
                    className="w-full flex-1"
                    data-testid="search"
                    type="search"
                    name="search"
                    id="search"
                    placeholder={text('lblSearch')}
                    aria-label={text('lblSearch')}
                    value={draftSearch}
                    onChange={updateDraftSearch}
                />
                <div className="flex gap-2">
                    <Button
                        data-testid="search-button"
                        type="submit"
                        label={text('lblSearch')}
                        icon={PrimeIcons.SEARCH}
                    />
                </div>
            </form>
            <Table
                value={displayedChargePoints}
                lazy
                paginator
                first={first}
                rows={rows}
                totalRecords={chargePoints?.totalCount ?? 0}
                onPage={onPage}
                loading={isLoadingChargePoints}
                scrollable
                scrollHeight="45vh"
                paginatorDropdownAppendTo="self"
                emptyMessage={text('lblNoChargersFound')}
                data-testid="add-charge-points-to-pool-table"
            >
                <Column field="id" header={text('lblSerialNumberColumn')} style={{ minWidth: '8rem' }} />
                <Column
                    field="chargePointStatus"
                    header={text('lblStatus')}
                    body={(chargePoint) => <ChargepointStatus chargePoint={chargePoint} />}
                />
                <Column field="name" header={text('lblName')} />
                <Column
                    field="settings"
                    header={text('lblSettings')}
                    body={(chargePoint) => <SettingsInfo chargePoint={chargePoint} />}
                />
                <Column
                    field="lastHeartbeat"
                    header={text('lblLastSeenTime')}
                    body={(chargePoint) =>
                        (chargePoint.chargePointStatus === 'Offline' &&
                            localizeDate(chargePoint.lastHeartbeat || '')) ||
                        '---'
                    }
                />
                <Column
                    field="add"
                    style={{ width: '5rem' }}
                    body={(chargePoint) => {
                        return (
                            <div className="flex justify-content-end">
                                <Button
                                    data-testid={`add-button-charger-${chargePoint.id}`}
                                    icon="pi pi-plus"
                                    aria-label={text('btnAdd')}
                                    size="small"
                                    outlined
                                    rounded
                                    severity="success"
                                    tooltip={
                                        chargePoint.isAssigned
                                            ? text('msgChargerAlreadyAssignedToPool')
                                            : text('btnAdd')
                                    }
                                    tooltipOptions={{ position: 'top', showOnDisabled: true }}
                                    loading={chargePoint.isPending}
                                    disabled={chargePoint.isPending || chargePoint.isAssigned}
                                    onClick={() => onAdd(chargePoint)}
                                />
                            </div>
                        )
                    }}
                />
            </Table>
        </Dialog>
    )
})
